/**
 * Integration adapter contracts (#67f P2 seam) — the pluggable interface every
 * backend adapter implements, one contract per capability DOMAIN, plus a registry
 * that resolves the adapters enabled for this build (`VITE_INTEGRATIONS`).
 *
 * Generalizes the pattern the org-import module already proved: a source-neutral
 * shape + a pluggable adapter, orchestration agnostic to the vendor
 * (`modules/orgImport/types.ts` → `OrgImportAdapter`). That IS the HCM domain's
 * live contract; this file provides the base + the OTHER domains' contracts + the
 * registry, so a new vendor is: implement the domain contract → `registerAdapter`.
 *
 * P1 (shipped): the catalog + `isIntegrationEnabled` + Client Setup selection
 * (`config/integrationsCatalog.ts`). This is P2: the CONTRACTS. Wiring each real
 * vendor adapter + credentials is per-vendor, on customer demand — the registry
 * starts empty; the JSON/manual connector is the universal fallback until then.
 */
import type { IntegrationDomain } from '@/config/integrationsCatalog'
import { isIntegrationEnabled, INTEGRATIONS_CATALOG } from '@/config/integrationsCatalog'
import type { MesIssueRow, PlmPartRow, PlmBomItemRow, PlmOperationRow } from '@/services/mdg/recon/reconTypes'

// ─── Base ─────────────────────────────────────────────────────────────────────

export interface AdapterHealth {
  ok: boolean
  /** Human-readable status (endpoint reachable, creds valid, last-sync, …). */
  detail?: string
}

/** Every adapter is identified by its catalog key and can report health. */
export interface IntegrationAdapter {
  /** Matches an `INTEGRATIONS_CATALOG` key (e.g. 'payroll.adp'). */
  readonly key: string
  readonly domain: IntegrationDomain
  readonly vendor: string
  /** Cheap liveness/creds probe for the Integrations status surface. */
  healthCheck(): Promise<AdapterHealth>
}

// ─── Source-neutral shapes for the non-HCM domains ──────────────────────────────
// (HCM reuses org-import's ImportedOrgUnit / ImportedPerson / ImportedRoleAssignment.)

export interface PayrollWorker {
  externalId: string
  name: string
  /** Payroll-system worker id (e.g. ADP associate id). */
  payrollId: string
  /** SAP employee number, when the payroll system carries the crosswalk (#104). */
  sapEmployeeNo?: string | null
  /**
   * Which payroll tenant/company this worker belongs to — e.g. an ADP company
   * code (DTC US Inc / CagWorks / Adaptive / Corp10). A given payrollId is only
   * unique WITHIN a tenant, so exports are partitioned per tenant (#119). Null /
   * omitted for a single-tenant customer.
   */
  payrollTenant?: string | null
}

export interface ChargeObject {
  externalId: string
  code: string
  name: string
  type?: string | null
  /** Linked WBS element, when the source provides it. */
  wbsElement?: string | null
  active: boolean
}

export interface WorkCalendarEntry {
  /** ISO date (business-tz calendar day). */
  date: string
  name: string
  kind: 'holiday' | 'non-working' | 'shift-pattern'
}

export interface LaborPosting {
  workerExternalId: string
  chargeObjectCode: string
  /** ISO date. */
  date: string
  hours: number
}

export interface OrderStatus {
  orderId: string
  status: string
  qtyComplete?: number
  qtyTotal?: number
}

export interface DemandSignal {
  sku: string
  /** Period key (e.g. 'W26' or ISO date). */
  period: string
  quantity: number
}

export interface OutboundMessage {
  to: string
  channel: 'sms' | 'email'
  subject?: string
  body: string
}

export interface PostResult {
  posted: number
  skipped: number
  errors: string[]
}

// Source-neutral payroll-export shapes (the "approved hours → payroll" wire format,
// produced by modules/timesheet/payroll/buildPayrollBatch.ts). Vendor-agnostic on
// purpose: REG/OT/DT/LV hours per worker per pay period + the payroll-id crosswalk.
// A vendor adapter (e.g. ADP) maps these canonical codes to its own earnings codes
// and serializes the file (e.g. EPIP) — that vendor step is separate from this shape.
export type PayrollEarningCode = 'REG' | 'OT' | 'DT' | 'LV'
export interface PayrollEarning {
  code: PayrollEarningCode
  hours: number
  /** For LV only: the source leave/absence type (e.g. SAP AWART); null for worked codes. */
  leaveType?: string | null
  /**
   * The SPECIFIC internal earnings code these hours were evaluated under
   * (`earnings_codes.code`, e.g. 'SHIFT-3RD', 'HOL-WORKED'), when a premium-aware
   * producer built the line. ADDITIVE + OPTIONAL (Time Evaluation Phase 4b) —
   * every pre-existing producer and consumer keeps working untouched.
   *
   * WHY: `code` is one of only four canonical buckets (REG/OT/DT/LV), which is
   * the right vendor-neutral shape but cannot name a 3rd-shift differential. The
   * Time Evaluation engine emits premium lines per earnings code, so
   * `payroll/evaluatedToPayrollBatch.ts` keeps the canonical bucket in `code`
   * (so `serializePayrollBatch` and `partitionPayrollBatch` still total correctly)
   * and puts the real code here. That is what lets the vendor-neutral QA preview
   * and the ADP paydata file be provably the same numbers instead of two
   * divergent truths.
   *
   * ⚠️ A premium line is bucketed under the canonical code of the hours it sits
   * on. So on a night shift, `code: 'REG'` carries BOTH the 8 base hours and the
   * 4 uplift-tagged premium hours, in two separate rows distinguished only by
   * `earningsCode`. `serializePayrollBatchCsv`'s `reg_hours` column therefore
   * sums to 12, which is the same 12 hour-cells the ADP file carries. Read
   * `earningsCode` when you need the two apart.
   *
   * Absent on lines built by `buildPayrollBatch.ts` (the pre-Phase-4 path), which
   * only knows the four canonical codes.
   */
  earningsCode?: string
}
export interface PayrollBatchLine {
  /** The worker's id in the source (timesheet) system. */
  workerExternalId: string
  /** Payroll-system worker id (e.g. ADP associate id) — the export key. */
  payrollId: string
  sapEmployeeNo?: string | null
  /** Payroll tenant (e.g. ADP company code); null = untagged / single-tenant. */
  payrollTenant?: string | null
  earnings: PayrollEarning[]
  totalHours: number
}
export interface PayrollBatch {
  period: { start: string; end: string; label: string }
  lines: PayrollBatchLine[]
}

// ─── Per-domain contracts ───────────────────────────────────────────────────────
// HCM: the canonical contract is `OrgImportAdapter` in modules/orgImport/types.ts
// (sourceSystem + pullOrgUnits/pullPeople/pullRoleAssignments). Not re-declared here
// to avoid divergence — an HCM vendor implements OrgImportAdapter + registers it.

export interface PayrollAdapter extends IntegrationAdapter {
  domain: 'payroll'
  pullWorkers(): Promise<PayrollWorker[]>
  /**
   * Push an approved-week payroll batch out to the payroll system (the export
   * leg — e.g. ADP EPIP file). Optional: a vendor implements it once the file
   * spec is in hand. The canonical PayrollBatch is produced by
   * modules/timesheet/payroll/buildPayrollBatch.ts (source-neutral).
   */
  pushPayrollBatch?(batch: PayrollBatch): Promise<PostResult>
}

export interface ErpFinanceAdapter extends IntegrationAdapter {
  domain: 'erp-finance'
  pullChargeObjects(): Promise<ChargeObject[]>
  pullWorkCalendar?(): Promise<WorkCalendarEntry[]>
  /** Write-back leg — post approved labor to CATS/CATZ (#63). */
  postApprovedLabor(postings: LaborPosting[]): Promise<PostResult>
}

/**
 * Scope of one reconciliation pull (Master Data Governance > PLM / MES
 * reconciliation). Every field is optional: an adapter narrows its query with
 * whatever the run supplies and returns the canonical rows.
 */
export interface ReconPullScope {
  plant?: string
  /** PLM: parts / parents to pull; MES: orders to pull. Empty = the adapter's default slice. */
  keys?: string[]
  /** MES: issues on or after this ISO timestamp. */
  since?: string
  /** PLM: effectivity date (YYYY-MM-DD). */
  keyDate?: string
}

export interface MesAdapter extends IntegrationAdapter {
  domain: 'mes'
  pullOrderStatus(): Promise<OrderStatus[]>
  /**
   * Component issue transactions per order, in the canonical shape the MES vs
   * SAP goods-issue reconciliation compares (services/mdg/recon/reconTypes.ts).
   * Optional: a client's MES adapter implements it during the implementation;
   * until then the reconciliation takes an uploaded extract.
   */
  pullComponentIssues?(scope: ReconPullScope): Promise<MesIssueRow[]>
}

/**
 * A client's PLM (Teamcenter, Windchill, 3DEXPERIENCE, ...). Returns released
 * parts, manufacturing BOM lines, and bill-of-process operations in the
 * canonical shapes the PLM vs SAP reconciliation compares. Built per client
 * during the implementation; the uploaded canonical extract stands in until then.
 */
export interface PlmAdapter extends IntegrationAdapter {
  domain: 'plm'
  pullReleasedData(scope: ReconPullScope): Promise<{
    parts: PlmPartRow[]
    bomItems: PlmBomItemRow[]
    operations: PlmOperationRow[]
  }>
}

export interface ScmAdapter extends IntegrationAdapter {
  domain: 'scm'
  pullDemandSignals(): Promise<DemandSignal[]>
}

export interface NotificationAdapter extends IntegrationAdapter {
  domain: 'notifications'
  send(message: OutboundMessage): Promise<{ delivered: boolean; providerId?: string }>
}

export interface SsoAdapter extends IntegrationAdapter {
  domain: 'sso'
  readonly protocol: 'oidc' | 'saml'
  /** IdP metadata / config the app auth layer consumes (SSO doesn't "pull" at runtime). */
  metadata(): Promise<Record<string, unknown>>
}

// ─── Registry seam ──────────────────────────────────────────────────────────────
// P2 fills this as real adapters land: `registerAdapter('payroll.adp', () => new AdpAdapter(cfg))`.
// Kept in a module-level map so an adapter registers itself at import; consumers ask
// the registry for the adapters enabled in this build.

type AnyAdapterFactory = () => IntegrationAdapter

const REGISTRY = new Map<string, AnyAdapterFactory>()

/** Register a vendor adapter under its catalog key (call at adapter-module import). */
export function registerAdapter(key: string, factory: AnyAdapterFactory): void {
  if (!INTEGRATIONS_CATALOG.some((i) => i.key === key)) {
    // Guard: an adapter must correspond to a catalogued integration.
    console.warn(`[integrations] registerAdapter: '${key}' is not in INTEGRATIONS_CATALOG — ignored.`)
    return
  }
  REGISTRY.set(key, factory)
}

/** The single adapter for a key, IF it's registered AND enabled for this build. */
export function getAdapter(key: string): IntegrationAdapter | null {
  if (!isIntegrationEnabled(key)) return null
  const factory = REGISTRY.get(key)
  return factory ? factory() : null
}

/** All registered adapters that are enabled for this build (skip disabled/unregistered). */
export function resolveEnabledAdapters(): IntegrationAdapter[] {
  const out: IntegrationAdapter[] = []
  for (const [key, factory] of REGISTRY) {
    if (isIntegrationEnabled(key)) out.push(factory())
  }
  return out
}

/** Enabled integrations that have NO adapter yet — surface these as "use the JSON/manual fallback". */
export function enabledKeysMissingAdapter(): string[] {
  return INTEGRATIONS_CATALOG
    .filter((i) => isIntegrationEnabled(i.key) && !REGISTRY.has(i.key))
    .map((i) => i.key)
}
